import React from 'react';
import { X, ExternalLink, FileText, DollarSign } from 'lucide-react';
import DocumentTimeline from './DocumentTimeline';
import PaymentHistoryTable from './PaymentHistoryTable';

const money = (v: any) => `\u09F3${Number(v || 0).toLocaleString('en-BD', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (v: any) => v ? new Date(v).toLocaleDateString('en-BD') : '-';

type DocumentType = 'receivable' | 'payable' | 'expense' | 'journal' | 'payment';

type QuickAction = {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  primary?: boolean;
};

type TimelineStep = {
  label: string;
  date?: string;
  active: boolean;
  description?: string;
};

type PaymentRecord = {
  date?: string;
  method?: string;
  account?: string;
  reference?: string;
  user?: string;
  amount: number;
  type?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  documentType: DocumentType;
  title: string;
  subtitle?: string;

  /** Header fields shown in a 2-column grid */
  fields: { label: string; value: React.ReactNode }[];

  /** Document status timeline */
  timeline: TimelineStep[];

  /** Payment records */
  payments?: PaymentRecord[];
  paymentsLoading?: boolean;

  /** Quick action buttons */
  actions?: QuickAction[];

  /** Journal entries tied to this document */
  journalEntries?: { entry_number: string; date?: string; reference_type?: string; total_debit?: number; total_credit?: number }[];
  journalLoading?: boolean;

  /** Source document link */
  sourceLink?: { label: string; url: string };

  /** Notes/description */
  notes?: string;

  /** Child content at the bottom (e.g. journal lines table) */
  children?: React.ReactNode;
};

export default function FinancialDocumentDrawer(props: Props) {
  if (!props.open) return null;

  const navigateTo = (url: string) => window.open(url, '_blank');

  return (
    <div className="acct-modal-overlay" onClick={props.onClose}>
      <div className="acct-modal" style={{ maxWidth: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="acct-modal-header" style={{ flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileText size={18} style={{ color: '#4f46e5' }} />
            <div>
              <h3 className="acct-modal-title" style={{ margin: 0 }}>{props.title}</h3>
              {props.subtitle && <span style={{ fontSize: '.75rem', color: '#64748b' }}>{props.subtitle}</span>}
            </div>
          </div>
          <button className="acct-modal-close" onClick={props.onClose}><X size={16} /></button>
        </div>

        <div className="acct-modal-body" style={{ overflowY: 'auto', flex: 1 }}>
          {/* Quick Actions */}
          {props.actions && props.actions.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              {props.actions.map((a, i) => (
                <button key={i} onClick={a.onClick}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '6px 12px', borderRadius: 7, border: 'none',
                    fontWeight: 600, fontSize: '.75rem', cursor: 'pointer',
                    background: a.primary ? '#4f46e5' : '#f1f5f9',
                    color: a.primary ? '#fff' : '#475569',
                    transition: 'all .15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = a.primary ? '#4338ca' : '#e2e8f0'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = a.primary ? '#4f46e5' : '#f1f5f9'; }}>
                  {a.icon}{a.label}
                </button>
              ))}
            </div>
          )}

          {/* Source Link */}
          {props.sourceLink && (
            <div style={{ marginBottom: 16, fontSize: '.82rem' }}>
              <strong style={{ color: '#64748b', fontSize: '.7rem', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Source Document</strong>
              <button onClick={() => navigateTo(props.sourceLink!.url)}
                style={{ border: 'none', background: '#eff6ff', color: '#1d4ed8', padding: '6px 12px', borderRadius: 7, cursor: 'pointer', fontWeight: 600, fontSize: '.8rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <ExternalLink size={13} /> {props.sourceLink.label}
              </button>
            </div>
          )}

          {/* Two-Column Layout: Fields + Timeline */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            {/* Fields */}
            <div>
              {props.fields.map((f, i) => (
                <div key={i} style={{ marginBottom: 8, fontSize: '.82rem' }}>
                  <strong style={{ color: '#64748b', display: 'block', fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 1 }}>{f.label}</strong>
                  <div style={{ fontWeight: 500 }}>{f.value}</div>
                </div>
              ))}
            </div>

            {/* Timeline */}
            <div>
              <strong style={{ color: '#64748b', display: 'block', fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>Timeline</strong>
              <DocumentTimeline steps={props.timeline} />
            </div>
          </div>

          {/* Notes */}
          {props.notes && (
            <div style={{ marginBottom: 16, padding: '8px 12px', background: '#f8fafc', borderRadius: 8, fontSize: '.8rem', color: '#475569' }}>
              <strong style={{ color: '#64748b', fontSize: '.68rem', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Notes</strong>
              {props.notes}
            </div>
          )}

          {/* Payment History */}
          {props.payments !== undefined && (
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '.82rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                <DollarSign size={14} style={{ color: '#64748b' }} /> Payment History
              </h4>
              <PaymentHistoryTable payments={props.payments} loading={props.paymentsLoading} />
            </div>
          )}

          {/* Journal Entries */}
          {props.journalEntries && props.journalEntries.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '.82rem' }}>Journal Entries</h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
                  <thead><tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', textAlign: 'left', fontSize: '.65rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Entry #</th>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', textAlign: 'left', fontSize: '.65rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Date</th>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', textAlign: 'right', fontSize: '.65rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Debit</th>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', textAlign: 'right', fontSize: '.65rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Credit</th>
                  </tr></thead>
                  <tbody>{props.journalEntries.map((je, i) => (
                    <tr key={i}>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', fontFamily: 'monospace', fontSize: '.72rem', fontWeight: 600 }}>{je.entry_number}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', fontSize: '.72rem' }}>{je.date ? date(je.date) : '-'}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>{je.total_debit ? money(je.total_debit) : '-'}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: '#059669', fontWeight: 600 }}>{je.total_credit ? money(je.total_credit) : '-'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {/* Children */}
          {props.children}
        </div>
      </div>
    </div>
  );
}
